import type { Account, AccountLedgerEntry, AccountType, Flow, Transaction } from '../types';
import { displayDate, monthKey, normalizeDateISO, nowISO } from './date';

export type EditEligibilityCode = 'editable' | 'manage_transfer' | 'manage_card_payment' | 'manage_plan' | 'manage_crdr' | 'read_only';

export type EditEligibility = {
  canEdit: boolean;
  code: EditEligibilityCode;
  label: string;
  reason: string;
};

export type EditableTransactionPatch = {
  date?: string;
  dateISO?: string;
  category?: string;
  amount?: number;
  paymentMode?: string;
  accountId?: string;
  accountName?: string;
  accountType?: AccountType | '';
  crdrPerson?: string;
  taxTag?: string;
  taxCategory?: string;
  reportGroup?: string;
  isTaxRelevant?: boolean;
  note?: string;
  attachmentUrl?: string;
  attachmentPath?: string;
  expectedUpdatedAtISO?: string;
};

export type NormalizedEditableTransaction = Pick<Transaction,
  'date' | 'dateISO' | 'displayDate' | 'monthKey' | 'type' | 'category' | 'amount' | 'paymentMode' |
  'accountId' | 'accountName' | 'accountType' | 'crdrPerson' | 'taxTag' | 'taxCategory' | 'reportGroup' |
  'isTaxRelevant' | 'note' | 'attachmentUrl' | 'attachmentPath'
>;

export type TransactionImpact = {
  accountId: string;
  amount: number;
  balanceDelta: number;
  availableLimitDelta: number;
  ledgerType: AccountLedgerEntry['type'];
  direction: AccountLedgerEntry['direction'];
};

export const editableTransactionSources = new Set(['', 'Manual', 'BulkEntry', 'Migration']);
export const editableTransactionTypes = new Set<Flow>(['Income', 'Expense', 'Investment']);

export function canEditTransaction(row: Partial<Transaction>): EditEligibility {
  if (!row || !row.id) return { canEdit: false, code: 'read_only', label: 'Read only', reason: 'Missing transaction identifier.' };
  if (row.status === 'deleted' || row.active === false) return { canEdit: false, code: 'read_only', label: 'Read only', reason: 'Deleted transactions cannot be edited.' };
  if (row.source === 'PlanPayment' || row.refId) return { canEdit: false, code: 'manage_plan', label: 'Manage in Plans', reason: 'Linked plan payments must be edited through Plans to preserve payment history.' };
  if (!editableTransactionTypes.has(row.type as Flow)) return { canEdit: false, code: 'read_only', label: 'Read only', reason: 'This record type is not a standard transaction.' };
  if (!editableTransactionSources.has(String(row.source || ''))) return { canEdit: false, code: 'read_only', label: 'Read only', reason: 'System-generated transactions are read only in this phase.' };
  return { canEdit: true, code: 'editable', label: 'Editable', reason: 'Standard income, expense, and investment transactions can be edited safely.' };
}

export const transferEditEligibility = (): EditEligibility => ({
  canEdit: false,
  code: 'manage_transfer',
  label: 'Manage Transfer',
  reason: 'Transfers are paired ledger entries and are not edited through the generic transaction editor.'
});

export const cardPaymentEditEligibility = (): EditEligibility => ({
  canEdit: false,
  code: 'manage_card_payment',
  label: 'Manage Card Payment',
  reason: 'Credit-card payments affect two accounts and are managed through card-payment flows.'
});

export const planPaymentEditEligibility = (): EditEligibility => ({
  canEdit: false,
  code: 'manage_plan',
  label: 'Manage in Plans',
  reason: 'Plan-generated payments stay linked to plan history and recurrence counters.'
});

export const crdrEditEligibility = (): EditEligibility => ({
  canEdit: false,
  code: 'manage_crdr',
  label: 'Manage CR/DR',
  reason: 'CR/DR entries use the CR/DR ledger model, not standard transaction mutation.'
});

export function normalizeEditableTransaction(original: Transaction, patch: EditableTransactionPatch): NormalizedEditableTransaction {
  const dateISO = normalizeDateISO(patch.dateISO || patch.date || original.dateISO || original.date);
  const amount = Number(patch.amount ?? original.amount ?? 0);
  if (amount <= 0) throw new Error('Amount must be greater than zero.');
  const type = original.type;
  if (!editableTransactionTypes.has(type)) throw new Error('Only income, expense, and investment transactions are editable in this phase.');
  if ((patch as Partial<Transaction>).type && (patch as Partial<Transaction>).type !== type) throw new Error('Changing transaction type is not supported in Phase 3A.');
  const taxTag = patch.taxTag ?? original.taxTag ?? '';
  return {
    date: dateISO,
    dateISO,
    displayDate: displayDate(dateISO),
    monthKey: monthKey(dateISO),
    type,
    category: String(patch.category ?? original.category ?? '').trim(),
    amount,
    paymentMode: String(patch.paymentMode ?? original.paymentMode ?? '').trim(),
    accountId: patch.accountId ?? original.accountId ?? '',
    accountName: patch.accountName ?? original.accountName ?? '',
    accountType: patch.accountType || original.accountType,
    crdrPerson: patch.crdrPerson ?? original.crdrPerson ?? '',
    taxTag,
    taxCategory: patch.taxCategory ?? original.taxCategory ?? '',
    reportGroup: patch.reportGroup ?? original.reportGroup ?? '',
    isTaxRelevant: patch.isTaxRelevant ?? Boolean(taxTag && taxTag !== 'Ignore for ITR'),
    note: patch.note ?? original.note ?? '',
    attachmentUrl: patch.attachmentUrl ?? original.attachmentUrl ?? '',
    attachmentPath: patch.attachmentPath ?? original.attachmentPath ?? ''
  };
}

export function financialImpact(row: Pick<Transaction, 'type' | 'amount' | 'accountId' | 'accountType'>): TransactionImpact | null {
  if (!row.accountId) return null;
  const amount = Number(row.amount || 0);
  if (amount <= 0) throw new Error('Amount must be greater than zero.');
  const accountType = row.accountType || 'bank';
  if (accountType === 'credit_card' && row.type !== 'Expense') throw new Error('Credit card account can only be used for Expense transactions.');
  const isIncome = row.type === 'Income';
  const isCreditCardSpend = accountType === 'credit_card' && row.type === 'Expense';
  return {
    accountId: row.accountId,
    amount,
    balanceDelta: isCreditCardSpend ? amount : isIncome ? amount : -amount,
    availableLimitDelta: isCreditCardSpend ? -amount : 0,
    ledgerType: isCreditCardSpend ? 'credit_card_spend' : row.type.toLowerCase() as AccountLedgerEntry['type'],
    direction: isIncome ? 'credit' : 'debit'
  };
}

export const reverseImpact = (impact: TransactionImpact | null): TransactionImpact | null =>
  impact ? { ...impact, balanceDelta: -impact.balanceDelta, availableLimitDelta: -impact.availableLimitDelta } : null;

export function applyImpactToAccount(account: Account, impact: TransactionImpact | null) {
  if (!impact) return account;
  if (account.id !== impact.accountId) throw new Error('Impact account mismatch.');
  const currentBalance = Number(account.currentBalance || 0) + impact.balanceDelta;
  return {
    ...account,
    currentBalance,
    availableLimit: account.type === 'credit_card'
      ? Math.max(Number(account.creditLimit || 0) - currentBalance, 0)
      : Number(account.availableLimit || 0)
  } satisfies Account;
}

export function assertActiveAccountForMutation(account: Pick<Account, 'name' | 'status' | 'active'>) {
  if (account.status !== 'active' || account.active === false) throw new Error(`${account.name || 'Selected account'} is not active.`);
}

export function buildTransactionEditAudit(original: Transaction, source = 'transaction_edit') {
  return {
    source,
    editedAtISO: nowISO(),
    previous: {
      amount: Number(original.amount || 0),
      type: original.type,
      accountId: original.accountId || '',
      accountName: original.accountName || '',
      accountType: original.accountType || '',
      dateISO: original.dateISO || original.date,
      category: original.category || '',
      paymentMode: original.paymentMode || ''
    }
  };
}

export function assertFreshTransactionVersion(original: Pick<Transaction, 'updatedAtISO'>, expectedUpdatedAtISO?: string) {
  if (expectedUpdatedAtISO && original.updatedAtISO && expectedUpdatedAtISO !== original.updatedAtISO) {
    throw new Error('This transaction changed since you opened it. Reload and try again.');
  }
}

export function hasBalanceImpactChange(original: Transaction, next: NormalizedEditableTransaction) {
  return Number(original.amount || 0) !== Number(next.amount || 0)
    || (original.accountId || '') !== (next.accountId || '')
    || original.type !== next.type;
}
